import { z } from 'zod';
import { normalizePhone } from '../phone';
import { localDateSchema } from './visits';

const optionalText = (max: number, message: string) =>
  z
    .string()
    .trim()
    .max(max, message)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

export const clientPhoneSchema = z.object({
  phone: z
    .string()
    .trim()
    .min(1, 'Wpisz numer telefonu.')
    .max(32, 'Numer telefonu jest za długi.')
    .refine((v) => normalizePhone(v) !== null, 'Wpisz poprawny numer telefonu, np. 600 123 456.'),
  label: optionalText(50, 'Opis numeru może mieć najwyżej 50 znaków.'),
});

export const clientObjectSchema = z.object({
  label: optionalText(100, 'Nazwa może mieć najwyżej 100 znaków.'),
  address: z.string().trim().min(3, 'Wpisz adres.').max(255, 'Adres może mieć najwyżej 255 znaków.'),
  lat: z.number().min(-90).max(90).nullable().optional(),
  lng: z.number().min(-180).max(180).nullable().optional(),
  note: optionalText(2000, 'Notatka może mieć najwyżej 2000 znaków.'),
  /** Next periodic inspection (local date); usually set when closing a visit. */
  nextInspectionDue: localDateSchema.nullable().optional(),
});
export type ClientObjectRequest = z.input<typeof clientObjectSchema>;

const clientFields = {
  /** Optional: a client may be just a phone number (shown in place of the name). */
  name: z
    .string()
    .trim()
    .max(200, 'Nazwa może mieć najwyżej 200 znaków.')
    .refine((v) => v === '' || v.length >= 2, 'Imię i nazwisko albo nazwa musi mieć co najmniej 2 znaki.')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
  email: z
    .string()
    .trim()
    .max(200)
    .transform((v) => (v === '' ? null : v))
    .refine((v) => v === null || z.email().safeParse(v).success, 'Wpisz poprawny adres e-mail.')
    .nullable()
    .optional(),
  note: optionalText(4000, 'Notatka może mieć najwyżej 4000 znaków.'),
  phones: z.array(clientPhoneSchema).max(5, 'Można zapisać najwyżej 5 numerów.'),
};

export const NAME_OR_PHONE_MESSAGE = 'Wpisz imię i nazwisko (albo nazwę firmy) lub numer telefonu.';

export const clientCreateSchema = z
  .object({
    ...clientFields,
    phones: clientFields.phones.default([]),
    objects: z.array(clientObjectSchema).max(20).default([]),
  })
  .refine((d) => Boolean(d.name) || d.phones.length > 0, { path: ['name'], message: NAME_OR_PHONE_MESSAGE });
export type ClientCreateRequest = z.input<typeof clientCreateSchema>;

/** `phones`, when given, replaces the whole list. Objects are edited one by one. The name-or-phone rule is checked against the merged result. */
export const clientUpdateSchema = z.object(clientFields).partial();
export type ClientUpdateRequest = z.input<typeof clientUpdateSchema>;

export const clientObjectUpdateSchema = clientObjectSchema.partial();
export type ClientObjectUpdateRequest = z.input<typeof clientObjectUpdateSchema>;

export const clientSearchQuerySchema = z.object({
  q: z.string().trim().max(100).default(''),
  limit: z.coerce.number().int().min(1).max(100).default(30),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
});
